/**
 * Authentication state.
 *
 * Holds the signed-in user and the organizations they belong to. On mount it
 * tries to restore a session from the stored refresh token, so a page reload
 * does not sign the user out.
 *
 * The user and their memberships are fetched in PARALLEL: they come from
 * different services (IAM and tenancy) and neither depends on the other, so
 * bootstrapping costs one round trip rather than two.
 *
 * THE FIRST-LOGIN STATE
 *
 * An account created by a platform administrator (an organization owner) or by
 * an owner (a member) starts on a temporary password. Signing in with it
 * succeeds, but the access token is scoped to the password-change endpoints
 * only - every other call answers 403 PASSWORD_CHANGE_REQUIRED.
 *
 * `mustChangePassword` on the context is what the router reads to pin such a
 * user to /change-password. It is a UX affordance, not the enforcement: the
 * gateway and every service refuse the restricted token independently, so a
 * user who edited the URL would simply see errors instead of the ERP.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, tenancyApi } from '../services/index.js';
import { onSessionExpired } from '../lib/apiClient.js';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setTenantId as persistTenantId,
  setTokens,
} from '../lib/tokenStore.js';

const AuthContext = globalThis.__AUTH_CONTEXT__ || (globalThis.__AUTH_CONTEXT__ = createContext(null));

/** 'loading' until we know whether there is a session; then 'ready'. */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [tenantId, setTenantIdState] = useState(() => getTenantId());
  /**
   * True while the signed-in account still holds a temporary password.
   *
   * Held separately from `user` because it is known from a login response
   * before /auth/me has been read, and because it must survive the
   * organization load being skipped.
   */
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const selectOrganization = useCallback((id) => {
    persistTenantId(id);
    setTenantIdState(id);
  }, []);

  /**
   * Loads the user and their organizations, and settles the active one.
   *
   * When a password change is pending the organization call is SKIPPED: a
   * restricted token would be refused by it, and the answer is not needed to
   * render the change-password screen. Once the password is replaced this runs
   * again in full.
   */
  const loadSession = useCallback(async () => {
    // Issued TOGETHER rather than one after the other. They are answered by
    // different services and neither depends on the other, so bootstrapping
    // costs ONE round trip instead of two — which, against a remote database,
    // is most of the wait between opening the app and being able to use it.
    //
    // A user still holding a temporary password carries a token scoped to the
    // password-change endpoints, so their organization call comes back 403.
    // That is an expected outcome on this path, not a failure: `me` is what
    // decides, and the rejection below is discarded once it has.
    const [profileResult, organizationsResult] = await Promise.allSettled([
      authApi.me(),
      tenancyApi.myOrganizations(),
    ]);

    if (profileResult.status === 'rejected') throw profileResult.reason;
    const profile = profileResult.value;

    if (profile?.mustChangePassword) {
      setUser(profile);
      setMustChangePassword(true);
      setOrganizations([]);
      return { profile, organizations: [], mustChangePassword: true };
    }

    // For a normal session this one is NOT optional: without it the switcher
    // and every tenant-scoped screen have nothing to work from, so a failure
    // here has to surface rather than leave the app half-loaded.
    if (organizationsResult.status === 'rejected') throw organizationsResult.reason;
    const orgs = organizationsResult.value;

    setUser(profile);
    setMustChangePassword(false);
    setOrganizations(orgs ?? []);

    // Reconcile the remembered organization against what the user can
    // actually reach: memberships change, and a stale id would 403 every
    // request until they noticed.
    const stored = getTenantId();
    const valid = (orgs ?? []).some((org) => org.id === stored);

    if (valid) setTenantIdState(stored);
    else if (orgs?.length) selectOrganization(orgs[0].id);
    else selectOrganization(null);

    return { profile, organizations: orgs ?? [], mustChangePassword: false };
  }, [selectOrganization]);

  const signOutLocally = useCallback(() => {
    clearTokens();
    persistTenantId(null);
    setUser(null);
    setOrganizations([]);
    setTenantIdState(null);
    setMustChangePassword(false);
    setStatus('ready');
  }, []);

  // Restore a session on first load.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!getRefreshToken()) {
        setStatus('ready');
        return;
      }

      try {
        // No access token in memory after a reload, but apiClient will refresh
        // transparently on the first 401.
        await loadSession();
      } catch {
        clearTokens();
        if (!cancelled) {
          setUser(null);
          setOrganizations([]);
        }
      } finally {
        if (!cancelled) setStatus('ready');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // The client tells us when a refresh has failed for good.
  useEffect(() => onSessionExpired(signOutLocally), [signOutLocally]);

  const signIn = useCallback(
    async (credentials) => {
      const result = await authApi.login(credentials);
      setTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });

      // First login on a temporary password. The token just stored works only
      // on the password-change endpoints, so do not try to load organizations
      // - the caller sends the user to /change-password instead.
      if (result.requiresPasswordChange || result.user?.mustChangePassword) {
        setUser(result.user);
        setMustChangePassword(true);
        setOrganizations([]);
        setStatus('ready');
        return { user: result.user, organizations: [], mustChangePassword: true };
      }

      const { organizations: orgs } = await loadSession();
      setStatus('ready');
      return { user: result.user, organizations: orgs, mustChangePassword: false };
    },
    [loadSession]
  );

  /**
   * Completes the forced first password change.
   *
   * The response carries a fresh, UNRESTRICTED token pair, so the user is not
   * made to sign in a second time - they go straight on into the ERP.
   */
  const completePasswordChange = useCallback(
    async ({ currentPassword, newPassword }) => {
      const result = await authApi.changePassword({ currentPassword, newPassword });

      if (result?.accessToken) {
        setTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      }

      // `mustChangePassword` is deliberately NOT cleared here.
      //
      // It is the flag that pins the user to /change-password, and RequireAuth
      // navigates to "/" the instant it clears. Clearing it before the session
      // has actually loaded therefore drops the user onto the application shell
      // while `organizations` is still the empty array set at sign-in - and
      // AppLayout reads an empty list as "you are not in an organization yet"
      // and shows a dead end with a Sign out button. Against a remote database
      // that window is a full round trip, which is more than long enough to
      // read it, believe it and act on it.
      //
      // loadSession() clears the flag itself, in the same batch as the
      // memberships that justify it, so the user goes from this form straight
      // to their dashboard with nothing in between.
      const { organizations: orgs } = await loadSession();
      setStatus('ready');
      return { organizations: orgs };
    },
    [loadSession]
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.logout(getRefreshToken());
    } catch {
      // Revoking server-side is best effort; the local session goes either way.
    }
    signOutLocally();
  }, [signOutLocally]);

  /** Called after creating an organization, so the switcher picks it up. */
  const refreshOrganizations = useCallback(async () => {
    const orgs = await tenancyApi.myOrganizations();
    setOrganizations(orgs ?? []);
    return orgs ?? [];
  }, []);

  const activeOrganization = useMemo(
    () => organizations.find((org) => org.id === tenantId) ?? null,
    [organizations, tenantId]
  );

  const value = useMemo(
    () => ({
      status,
      isAuthenticated: Boolean(user),
      user,
      setUser,
      organizations,
      activeOrganization,
      tenantId,
      hasOrganization: organizations.length > 0,
      /** Pins the user to /change-password until the temporary one is replaced. */
      mustChangePassword,
      selectOrganization,
      refreshOrganizations,
      signIn,
      completePasswordChange,
      signOut,
      /** True when a session might be restorable but has not been yet. */
      isRestoring: status === 'loading' && Boolean(getRefreshToken()) && !getAccessToken(),
    }),
    [
      status,
      user,
      organizations,
      activeOrganization,
      tenantId,
      mustChangePassword,
      selectOrganization,
      refreshOrganizations,
      signIn,
      completePasswordChange,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
